import { invoiceBalance, invoiceStatus, quoteTotal, roundMoney, type Invoice, type Payment, type Quote } from '../types';
import {thaiDate,thaiDateAfter} from '../../shared/thaiDate';

export type DocumentNumberKind = 'quote' | 'invoice' | 'receipt';

const prefixes: Record<DocumentNumberKind, string> = { quote: 'QT', invoice: 'INV', receipt: 'RE' };

export const formatDocumentNumber = (kind: DocumentNumberKind, year: number, sequence: number) =>
  `${prefixes[kind]}-${year}-${String(sequence).padStart(4, '0')}`;

export const parseDocumentSequence = (kind: DocumentNumberKind, year: number, value: string) => {
  const match = value.match(new RegExp(`^${prefixes[kind]}-${year}-(\\d+)$`, 'i'));
  return match ? Number(match[1]) : 0;
};

export const createInvoiceRecord = (quote: Quote, number: string, now = new Date()): Invoice => {
  if (quote.status !== 'accepted' || !quote.acceptance?.confirmed) throw new Error('ต้องบันทึกการยอมรับใบเสนอราคาก่อนสร้างใบแจ้งหนี้');
  if (quoteTotal(quote) <= 0) throw new Error('ยอดรวมใบเสนอราคาต้องมากกว่า 0 บาท');
  const timestamp = now.toISOString();
  return {
    id: crypto.randomUUID(), number, quoteId: quote.id, quoteSnapshot: structuredClone(quote),
    issueDate: thaiDate(now), dueDate: thaiDateAfter(14,now), status: 'unpaid',
    createdAt: timestamp, updatedAt: timestamp, version: 1, deletedAt: null, syncState: 'pending',
  };
};

export type PaymentDraft = Pick<Payment, 'paidAt' | 'amount' | 'method' | 'reference' | 'note' | 'attachment'>;

export const createPaymentRecord = (
  invoice: Invoice,
  payments: Payment[],
  draft: PaymentDraft,
  receiptNumber: string,
  now = new Date(),
): { payment: Payment; nextStatus: Invoice['status'] } => {
  const amount = roundMoney(Number(draft.amount));
  const balance = invoiceBalance(invoice, payments);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error('ยอดรับชำระต้องมากกว่า 0 บาท');
  if (amount > balance + 0.001) throw new Error('ยอดรับชำระเกินยอดคงเหลือ');
  const payment: Payment = {
    id: crypto.randomUUID(), invoiceId: invoice.id, receiptNumber, ...draft, amount,
    createdAt: now.toISOString(), voidedAt: null,
  };
  return { payment, nextStatus: invoiceStatus(invoice, [...payments, payment]) };
};

export const createRevision = (quote: Quote, allQuotes: Quote[], now = new Date()): Quote => {
  if (quote.status !== 'accepted') throw new Error('สร้างฉบับแก้ไขได้เฉพาะใบเสนอราคาที่ถูกยอมรับแล้ว');
  const rootNumber = quote.number.replace(/-R\d+$/, '');
  const escaped = rootNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const revisions = allQuotes.map((item) => Number(item.number.match(new RegExp(`^${escaped}-R(\\d+)$`))?.[1] ?? 0));
  const revision = Math.max(quote.revision ?? 0, ...revisions, 0) + 1;
  const timestamp = now.toISOString();
  return {
    ...structuredClone(quote), id: crypto.randomUUID(), number: `${rootNumber}-R${revision}`,
    status: 'draft', acceptance: null, revision, revisedFromId: quote.id, replacedById: null,
    createdAt: timestamp, updatedAt: timestamp, version: 1, deletedAt: null, syncState: 'pending',
  };
};
